/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { type AdminRouterItem, routes } from '../../router';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROLES } from '../../constants/roles';

// Define styles for the sidebar
const useStyles = makeStyles({
  sider: {
    backgroundColor: tokens.colorNeutralBackground1,
    width: '220px',
    height: '100vh',
    overflowY: 'auto',
    padding: tokens.spacingVerticalM,
    transition: 'width 0.3s ease',

    '@media (max-width: 768px)': {
      width: '60px',
      padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXS}`,
    }
  },
  navItem: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemText: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,

    '@media (max-width: 768px)': {
      display: 'none',
    }
  },
  selectedNavItem: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  nestedNavItem: {
    paddingLeft: tokens.spacingHorizontalL,

    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalS,
    }
  },
  navItemIcon: {
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: tokens.spacingHorizontalS,

    '@media (max-width: 768px)': {
      fontSize: '18px',
      margin: '0 auto',
    }
  },
  navItemContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',

    '@media (max-width: 768px)': {
      justifyContent: 'center',
    }
  }
});

const ROLE_STORAGE_KEY = 'userRole';

const getCurrentRole = (): string[] => {
  try {
    const raw = sessionStorage.getItem(ROLE_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as string[];

    if (Array.isArray(parsed)) {
      return parsed.map(r => r.trim());
    }

    return [];
  } catch (error) {
    return [];
  }
};

const roleMatches = (allowed?: typeof ROLES, currentRoles?: string[]): boolean => {
  if (!allowed || allowed.length === 0) {
    return true;
  }

  if (!currentRoles || currentRoles.length === 0) {
    return false;
  }

  // Case-insensitive comparison
  const allowedLower = allowed.map(r => r.toLowerCase().trim());
  const currentRolesLower = currentRoles.map(r => r.toLowerCase().trim());
  return currentRolesLower.some(role => allowedLower.includes(role));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getMenuItems = (routes: AdminRouterItem[], parentPath = '', currentRoles?: string[]): any[] => {
  return routes
    .map(itm => {
      if (!itm.meta) {
        return null;
      }

      if (itm.meta.hideInMenu) {
        return null;
      }

      if (!roleMatches(itm.meta.allowedRoles, currentRoles)) {
        return null;
      }

      const fullPath = parentPath + itm.path;
      const children = itm.children ? getMenuItems(itm.children, fullPath + '/', currentRoles) : null;

      return {
        ...itm.meta,
        content: itm.meta.label,
        key: itm.meta.key,
        path: fullPath,
        items: children,
      };
    })
    .filter(itm => !!itm);
};

const PageSidebar = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>(getCurrentRole());

  useEffect(() => {
    const handleRoleChange = () => {
      const newRoles = getCurrentRole();
      setRoles(newRoles);
    };

    window.addEventListener('role-changed', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);

    return () => {
      window.removeEventListener('role-changed', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, []);

  const menuItems = useMemo(() => {
    const items = getMenuItems(routes, '', roles);
    return items;
  }, [roles]);

  const onSwitchMenu = (key: string) => {
    navigate(key);
  };

  useEffect(() => {
    setSelectedKeys([location.pathname]);
  }, [location.pathname]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderMenuItems = (items: any[], depth = 0) => {
    return items.map(item => (
      <div key={item.key}>
        <div
          className={`${styles.navItem} ${selectedKeys.includes(item.key) ? styles.selectedNavItem : ''} ${depth > 0 ? styles.nestedNavItem : ''}`}
          onClick={() => onSwitchMenu(item.key)}
          title={window.innerWidth < 768 ? item.content : undefined}
        >
          <div className={styles.navItemContainer}>
            {item.icon && <span className={styles.navItemIcon}>{item.icon}</span>}
            <span className={styles.navItemText}>{item.content}</span>
          </div>
        </div>
        {item.items && item.items.length > 0 && (
          <div className={styles.nestedNavItem}>
            {renderMenuItems(item.items, depth + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className={styles.sider}>
      {renderMenuItems(menuItems)}
    </div>
  );
};

export default PageSidebar;
